<script>
    import { SENSOR_INCIDENT_CAUSE_LABELS, SensorIncidentCause } from '@/enums/TinyPumper';

    /**
     * The KPI row above the incident grid.
     *
     * Ingestion failures get their own card, styled apart from the rest, because they are the only
     * bucket on this page engineering can fix: everything else is a device, a battery or a vendor,
     * and reading a GreaseBook bug as one more silent tank is how it stays unfixed.
     */
    export default {
        name: 'TinyPumperIncidentTotals',
        props: {
            // null means "no answer yet or the request failed" and is deliberately distinct from an
            // object of zeros, which means "asked, and the fleet is healthy".
            totals: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            hasFailed: {
                type: Boolean,
                default: false
            }
        },
        emits: ['filter-cause'],
        computed: {
            getVendorCard() {
                // "Delivering" is a claim about the vendor. Do not make it before we have an answer:
                // this card is the one that tells the operator whether the rest of the page is worth
                // reading at all.
                if (this.isUnknown) {
                    return {
                        label: 'LevelCon',
                        value: 'Unknown',
                        iconClass: 'bi-cloud-fill text-muted',
                        title: 'We could not read the vendor status.'
                    };
                }

                const isDown = !!this.totals?.isVendorDown;

                return {
                    label: 'LevelCon',
                    value: isDown ? 'Outage' : 'Delivering',
                    iconClass: isDown ? 'bi-cloud-slash-fill text-danger' : 'bi-cloud-check-fill text-primary',
                    title: isDown
                        ? 'A vendor-scope incident is open: LevelCon is not delivering, so per-device conclusions are meaningless right now.'
                        : 'No vendor-scope incident is open.'
                };
            },
            isUnknown() {
                return this.hasFailed || !this.totals;
            },
            /** An em dash, never 0: a number the operator can read as a fact must be one. */
            count() {
                return field => (this.isUnknown ? '—' : this.totals?.[field] || 0);
            },
            getCauses() {
                return (this.totals?.byCause || [])
                    .filter(cause => cause.cause !== SensorIncidentCause.IngestionFailure)
                    .map(cause => ({
                        ...cause,
                        label: SENSOR_INCIDENT_CAUSE_LABELS[cause.cause] || 'Unknown'
                    }));
            },
            getIngestionFailures() {
                return this.totals?.ingestionFailures || 0;
            },
            getIngestionCause() {
                return SensorIncidentCause.IngestionFailure;
            }
        },
        methods: {
            handleCauseClick(cause) {
                this.$emit('filter-cause', cause);
            }
        }
    };
</script>

<template>
    <div class="tiny-pumper-incident-totals">
        <div v-if="hasFailed" class="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3" role="alert">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>Could not load the incident totals. The numbers below are unknown, not zero — reload to try again.</span>
        </div>

        <div class="row g-3">
            <div class="col-6 col-md-4 col-xl-2">
                <div class="card card-body rounded-3 h-100" :title="getVendorCard.title">
                    <div class="text-muted small text-uppercase">{{ getVendorCard.label }}</div>
                    <div class="d-flex align-items-center gap-2 mt-1">
                        <i :class="`bi ${getVendorCard.iconClass} fs-5`"></i>
                        <span class="fw-bold">{{ getVendorCard.value }}</span>
                    </div>
                </div>
            </div>

            <div class="col-6 col-md-4 col-xl-2">
                <div class="card card-body rounded-3 h-100" title="Open incidents that are not currently snoozed.">
                    <div class="text-muted small text-uppercase">Open</div>
                    <div class="d-flex align-items-baseline gap-2 mt-1">
                        <span class="fs-4 fw-bold">{{ count('open') }}</span>
                        <small class="text-muted">{{ count('affectedSensors') }} sensors</small>
                    </div>
                </div>
            </div>

            <div class="col-6 col-md-4 col-xl-2">
                <div class="card card-body rounded-3 h-100" title="Deliberately silenced until a date, with a reason and an author.">
                    <div class="text-muted small text-uppercase">Snoozed</div>
                    <div class="d-flex align-items-baseline gap-2 mt-1">
                        <span class="fs-4 fw-bold">{{ count('snoozed') }}</span>
                        <small class="text-muted">{{ count('acknowledged') }} acknowledged</small>
                    </div>
                </div>
            </div>

            <div class="col-6 col-md-4 col-xl-2">
                <div
                    class="card card-body rounded-3 h-100"
                    title="Open for more than 30 days. Nobody is coming back for these on their own."
                >
                    <div class="text-muted small text-uppercase">Chronic</div>
                    <div class="d-flex align-items-baseline gap-2 mt-1">
                        <span class="fs-4 fw-bold">{{ count('chronic') }}</span>
                        <small class="text-muted">&gt; 30 days</small>
                    </div>
                </div>
            </div>

            <div class="col-12 col-md-8 col-xl-4">
                <component
                    :is="getIngestionFailures > 0 ? 'button' : 'div'"
                    :type="getIngestionFailures > 0 ? 'button' : null"
                    class="card card-body rounded-3 h-100 ingestion-card text-start w-100"
                    :class="!isUnknown && getIngestionFailures > 0 ? 'ingestion-card--open' : ''"
                    @click="getIngestionFailures > 0 ? handleCauseClick(getIngestionCause) : null"
                >
                    <div class="d-flex align-items-center gap-2">
                        <i class="bi bi-bug-fill fs-5"></i>
                        <div class="small text-uppercase fw-bold">Ingestion failures</div>
                    </div>
                    <div class="d-flex align-items-baseline gap-2 mt-1">
                        <span class="fs-4 fw-bold">{{ isUnknown ? '—' : getIngestionFailures }}</span>
                        <small>{{ isUnknown ? 'not known' : getIngestionFailures > 0 ? 'engineering-actionable' : 'nothing on us right now' }}</small>
                    </div>
                    <!-- Was a title attribute. The one sentence that says whose problem this is should
                         not require a hover the operator will not perform. -->
                    <small class="text-muted mt-1">LevelCon has the data and GreaseBook does not. Ours to fix — not a field visit.</small>
                </component>
            </div>
        </div>

        <!-- Renders whenever there is any cause to show. getCauses deliberately excludes ingestion
             (it has its own card), so gating the row on getCauses.length hid the ingestion chip in
             the one state engineering most needs to see: ingestion failures and nothing else. -->
        <div v-if="getCauses.length || getIngestionFailures > 0" class="d-flex flex-wrap align-items-center gap-2 mt-3">
            <span class="text-muted small text-uppercase me-1">Open by cause</span>
            <button
                v-for="cause in getCauses"
                :key="`incident-cause-${cause.cause}`"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :title="`${cause.affectedSensors} sensor(s)`"
                @click="handleCauseClick(cause.cause)"
            >
                {{ cause.label }}
                <span class="badge text-bg-light ms-1">{{ cause.count }}</span>
            </button>
            <button
                v-if="getIngestionFailures > 0"
                type="button"
                class="btn btn-sm btn-outline-danger"
                @click="handleCauseClick(getIngestionCause)"
            >
                Ingestion failure
                <span class="badge text-bg-light ms-1">{{ isUnknown ? '—' : getIngestionFailures }}</span>
            </button>
            <span v-if="isLoading" class="spinner-border spinner-border-sm text-muted ms-2" role="status"></span>
        </div>
    </div>
</template>

<style lang="scss">
    .tiny-pumper-incident-totals {
        .ingestion-card {
            border-style: dashed;

            &--open {
                border-color: var(--bs-danger-border-subtle);
                border-style: solid;
                background-color: var(--bs-danger-bg-subtle);
                color: var(--bs-danger-text-emphasis);
            }
        }
    }
</style>
